import type { HstCodeComponent, HstCodeTier } from 'ts-back-end/src/business/payer/dto/payer.dto'

/**
 * The tiered HST codes. Mirrors the backend's payer.const: a Split HST
 * configuration prices TC and PC per tier, which bill studies from 2027-01-01
 * on in place of a Global configuration's own CPT.
 */
export const HST_CODE_COMPONENTS: HstCodeComponent[] = ['TC', 'PC']
export const HST_CODE_TIERS: HstCodeTier[] = ['LOW', 'MODERATE', 'HIGH']

// Placeholders until the AMA publishes the final numbers.
export const HST_TIERED_CPT_CODES: Record<HstCodeComponent, Record<HstCodeTier, string>> = {
  TC: { LOW: '95X18', MODERATE: '95X19', HIGH: '95X20' },
  PC: { LOW: '95X21', MODERATE: '95X22', HIGH: '95X23' },
}

// A Split configuration starts on or after this day. Mirrors the backend.
export const HST_CODES_EARLIEST_EFFECTIVE_FROM = '2027-01-01'

export const HST_CODE_TIER_LABELS: Record<HstCodeTier, string> = {
  LOW: 'Low',
  MODERATE: 'Moderate',
  HIGH: 'High',
}

export type HstTierPrice = { billed: string; rate: string }

export type HstTierPrices = Record<HstCodeTier, Record<HstCodeComponent, HstTierPrice>>

export const hstTierCptCodes = (tier: HstCodeTier) =>
  HST_CODE_COMPONENTS.map((component) => HST_TIERED_CPT_CODES[component][tier]).join(' + ')

export const emptyHstTierPrices = (): HstTierPrices =>
  Object.fromEntries(
    HST_CODE_TIERS.map((tier) => [
      tier,
      Object.fromEntries(
        HST_CODE_COMPONENTS.map((component) => [component, { billed: '', rate: '' }]),
      ),
    ]),
  ) as HstTierPrices

export const isAmount = (value: string) =>
  value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0

const everyPrice = (prices: HstTierPrices, test: (price: HstTierPrice) => boolean) =>
  HST_CODE_TIERS.every((tier) =>
    HST_CODE_COMPONENTS.every((component) => test(prices[tier][component])),
  )

/** Whether every tier has a TC and PC billed amount, and any rate typed is a valid one. */
export const isHstTierPricesComplete = (prices: HstTierPrices) =>
  everyPrice(
    prices,
    (price) => isAmount(price.billed) && (price.rate.trim() === '' || isAmount(price.rate)),
  )

const sumOrNull = (amounts: (number | null)[]) =>
  amounts.every((amount) => amount !== null)
    ? amounts.reduce<number>((sum, amount) => sum + Number(amount), 0)
    : null

/** A completed study's TC + PC total in a tier; null until both are set. */
export const hstTierTotal = (prices: HstTierPrices, tier: HstCodeTier, field: keyof HstTierPrice) =>
  sumOrNull(
    HST_CODE_COMPONENTS.map((component) => {
      const value = prices[tier][component][field]

      return isAmount(value) ? Number(value) : null
    }),
  )
